/**
 * wet_drive — steps THE ONE WET CLOCK (`wet_state.wetClock`) from the weather and pushes its levels to the ground's
 * uniforms (`wetness.setWetLevels`) and the props' (`wet_layer` uWetA / uWetB / uWetGate / uRainFrom). Kept apart
 * from `wet_layer.ts` so the material graph (composed at parse, bundled by harnesses) never pulls the ground's whole
 * module in. WeatherModule calls `driveWetClock` once a frame; a `false` return means the flag is off and the
 * caller keeps its old lerp, bit for bit (shader gaps program round 3, weather surfaces, 2026-09-30).
 */
import * as THREE from 'three/webgpu';
import { wetClock, rainRateMmH, windMsFrom, sunFrom, nightCoolC } from './wet_state.js';
import { precipShear } from './rain_shelter.js';
import { setWetLevels, setWetness, getWetness } from './wetness.js';
import { uWetA, uWetB, uWetGate, uRainFrom, wetSurfacesMode } from './wet_layer.js';

// ── the clock's drive (WeatherModule calls this every frame when the flag is on) ──

export interface WetDriveInputs {
  /** The live precipitation kind + density (the emitters'). */
  kind: string;
  density: number;
  /** The weather's wind: unit xz + strength 0..1. */
  windX: number;
  windZ: number;
  windStrength: number;
  /** The meteo at the player (null before the first resolve). */
  tempC: number | null;
  rh: number | null;
  /** The sky: night factor 0..1 and cloud cover 0..1. */
  nightFactor: number;
  cover: number;
  /** The resolver's wetness target (the old lerp's) — its non-rain part is the floor. */
  targetWetness: number;
  /** The weather is live here (the Commons, not in space). */
  inCommons: boolean;
}

/** Probes: `frozen` holds the pinned clock (the drive pushes the levels without stepping). */
export const wetDriveControl = { frozen: false };

export const wetDriveStats = { steps: 0, lastRainMmH: 0, floor: 0, ground: { absorbed: 0, film: 0, pond: 0 } };

/**
 * Step the one clock and push its levels to the ground's and the props' uniforms. Returns false (and touches
 * nothing) when the flag is off — the caller keeps its old lerp, bit for bit.
 */
export function driveWetClock(dtS: number, i: WetDriveInputs): boolean {
  if (wetSurfacesMode() === 'off') return false;
  const rain = i.inCommons ? rainRateMmH(i.kind, i.density) : 0;
  const rainPart = i.kind === 'rain' ? Math.max(0, i.density) * 0.85 : 0;
  const floor = i.inCommons ? Math.max(0, Math.min(1, i.targetWetness - rainPart)) : 0;
  if (!wetDriveControl.frozen) wetClock.step({
    rainMmH: rain,
    tempC: i.tempC ?? 16,
    rh: i.rh ?? 0.6,
    windMs: windMsFrom(i.windStrength),
    sun: sunFrom(i.nightFactor, i.cover),
    cool: nightCoolC(i.nightFactor, i.cover),
    floor01: floor,
  }, dtS);
  const g = wetClock.groundUniforms();
  setWetLevels(g.absorbed, g.film, g.pond);
  const st = wetClock.levels('stone'), wd = wetClock.levels('wood'), fb = wetClock.levels('fabric');
  const mt = wetClock.levels('metal'), gl = wetClock.levels('glass');
  (uWetA.value as THREE.Vector4).set(Math.max(floor * 0.5, st.absorbed01), st.film01, Math.max(floor * 0.5, wd.absorbed01), wd.film01);
  (uWetB.value as THREE.Vector4).set(Math.max(floor * 0.5, fb.absorbed01), fb.film01, mt.film01, gl.film01);
  (uWetGate as unknown as { value: number }).value = i.inCommons ? 1 : 0;
  // the rain comes FROM up and upwind — the same slant the drops fall along (precipShear, the emitters' lean)
  const [sx, sz] = precipShear(i.kind === 'none' || i.kind === 'dust' ? 'rain' : i.kind, i.windX, i.windZ, i.windStrength);
  (uRainFrom.value as THREE.Vector3).set(-sx, 1, -sz).normalize();
  wetDriveStats.steps++; wetDriveStats.lastRainMmH = rain; wetDriveStats.floor = floor;
  wetDriveStats.ground = g;
  return true;
}

/** Hand the ground back to the old single number (the flag switched off live): film = pond = the wetness. */
export function releaseWetClock(): void { setWetness(getWetness()); (uWetGate as unknown as { value: number }).value = 0; }
